import {
  ActionConfirmationModal,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  InlineEditableField,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Typography,
} from "@/components/ui";
import {
  retrieveMatterTaskPlansQueryOptions,
  useCreateMatterTaskPlanTask,
  useDeleteMatterTaskPlanTask,
  useUpdateMatterTaskPlanTask,
} from "@/lib/data/react-query/matters/matter-task-plan";
import { MatterTaskPlanTaskSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  buildMatterStatusGroups,
  getMatterStatusCategoryLabel,
} from "@/lib/data/static/matterStatics";
import { MATTER_TASK_PLAN_TASK_TRIGGER_TYPE_OPTIONS } from "@/lib/data/static/matters/taskPlanTaskStatics";
import { useMatterTaskPlanActions } from "@/hooks/use-matter-task-plan-actions";
import type { MatterSettingStaticOption } from "@/lib/data/requests/matters/matter-setting-statics";
import type {
  MatterRole,
  MatterStatus,
  MatterTaskPlan,
  MatterTaskPlanTask,
} from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { startCase } from "lodash";
import { Check, Trash2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const taskPlanTaskFormSchema = z
  .object({
    title: MatterTaskPlanTaskSchema.shape.title,
    description: MatterTaskPlanTaskSchema.shape.description,
    triggerStatusId: MatterTaskPlanTaskSchema.shape.triggerStatusId.optional(),
    triggerSubitem: MatterTaskPlanTaskSchema.shape.triggerSubitem.optional(),
    matterTaskPlanTaskTriggerType: z.enum(
      MATTER_TASK_PLAN_TASK_TRIGGER_TYPE_OPTIONS,
    ),
  })
  .superRefine((data, ctx) => {
    if (
      data.matterTaskPlanTaskTriggerType === "matterStatus" &&
      !data.triggerStatusId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Matter status is required",
        path: ["triggerStatusId"],
      });
    }
    if (
      data.matterTaskPlanTaskTriggerType === "actionTaken" &&
      !data.triggerSubitem
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Action taken is required",
        path: ["triggerSubitem"],
      });
    }
  });

const defaultTaskPlanTaskValues: z.infer<typeof taskPlanTaskFormSchema> = {
  title: "",
  description: "",
  triggerStatusId: undefined,
  triggerSubitem: undefined,
  matterTaskPlanTaskTriggerType: "matterStatus",
};

const taskPlanTaskInlineSchemas = {
  title: MatterTaskPlanTaskSchema.shape.title,
  description: MatterTaskPlanTaskSchema.shape.description,
};

const onboardingTriggerTypeOptions = [
  ...MATTER_TASK_PLAN_TASK_TRIGGER_TYPE_OPTIONS,
].sort((left, right) => {
  if (left === "taskCompletion") return 1;
  if (right === "taskCompletion") return -1;
  return 0;
});

const renderMatterStatusOptions = (statuses: MatterStatus[]) =>
  buildMatterStatusGroups(statuses).map((group) => (
    <SelectGroup key={group.category}>
      <SelectLabel>{getMatterStatusCategoryLabel(group.category)}</SelectLabel>
      {group.items.map((status) => (
        <SelectItem key={status.value} value={status.value}>
          {status.label}
        </SelectItem>
      ))}
    </SelectGroup>
  ));
const triggerTypeLabel = (
  option: (typeof MATTER_TASK_PLAN_TASK_TRIGGER_TYPE_OPTIONS)[number],
) => (option === "taskCompletion" ? "Task completion" : startCase(option));

const getAssignmentPayload = (task: MatterTaskPlanTask) => ({
  assigneeRoleId: task.assigneeRoleId,
  assigneeUserId: task.assigneeUserId,
  assigneeBackupUserId: task.assigneeBackupUserId,
  assignerRoleId: task.assignerRoleId,
  assignerUserId: task.assignerUserId,
  assignerBackupUserId: task.assignerBackupUserId,
});

type AddTaskPlanTaskEditorProps = {
  taskPlanId: string;
  statuses: MatterStatus[];
  actionOptions: MatterSettingStaticOption[];
  areActionOptionsLoading: boolean;
  isActionOptionsError: boolean;
  isReadOnly: boolean;
  defaultAssigneeRoleId: string | null;
  onClose: () => void;
};

function AddTaskPlanTaskEditor({
  taskPlanId,
  statuses,
  actionOptions,
  areActionOptionsLoading,
  isActionOptionsError,
  isReadOnly,
  defaultAssigneeRoleId,
  onClose,
}: AddTaskPlanTaskEditorProps) {
  const createTask = useCreateMatterTaskPlanTask({ showToast: false });
  const isFormLoading = createTask.isPending;
  const form = useForm<z.infer<typeof taskPlanTaskFormSchema>>({
    resolver: zodResolver(taskPlanTaskFormSchema),
    defaultValues: defaultTaskPlanTaskValues,
    mode: "onSubmit",
  });

  const triggerType = form.watch("matterTaskPlanTaskTriggerType");
  const isMissingAssigneeRole = !defaultAssigneeRoleId;

  const createErrorMessage = useMemo(() => {
    const errors = [
      form.formState.errors.title?.message,
      form.formState.errors.matterTaskPlanTaskTriggerType?.message,
      form.formState.errors.triggerStatusId?.message,
      form.formState.errors.triggerSubitem?.message,
    ].filter(Boolean);

    return errors.length > 0 ? String(errors[0]) : null;
  }, [form.formState.errors]);

  const resetTriggerFields = () => {
    form.setValue("triggerStatusId", undefined);
    form.setValue("triggerSubitem", undefined);
  };

  const setDefaultTriggerValue = useCallback(
    (
      nextTriggerType: z.infer<
        typeof taskPlanTaskFormSchema
      >["matterTaskPlanTaskTriggerType"],
    ) => {
      if (nextTriggerType === "matterStatus") {
        const defaultStatusId = statuses[0]?.id;
        if (defaultStatusId) {
          form.setValue("triggerStatusId", defaultStatusId);
        }
        return;
      }
      const defaultAction = actionOptions[0]?.value;
      if (defaultAction) {
        form.setValue("triggerSubitem", defaultAction);
      }
    },
    [actionOptions, form, statuses],
  );

  useEffect(() => {
    if (
      triggerType === "matterStatus" &&
      !form.getValues("triggerStatusId") &&
      statuses.length > 0
    ) {
      setDefaultTriggerValue("matterStatus");
    }
    if (triggerType === "actionTaken" && !form.getValues("triggerSubitem")) {
      setDefaultTriggerValue("actionTaken");
    }
  }, [form, setDefaultTriggerValue, statuses, triggerType]);

  const onSubmit = async (data: z.infer<typeof taskPlanTaskFormSchema>) => {
    if (isMissingAssigneeRole) {
      form.setError("title", {
        message: "Add a matter role in settings to create tasks.",
      });
      return;
    }

    await createTask.mutateAsync({
      taskPlanId,
      title: data.title,
      description: data.description,
      assigneeRoleId: defaultAssigneeRoleId,
      triggerStatusId:
        triggerType === "matterStatus" ? data.triggerStatusId : undefined,
      triggerSubitem:
        triggerType === "actionTaken" ? data.triggerSubitem : undefined,
      triggerTaskId: undefined,
    });

    onClose();
    form.reset(defaultTaskPlanTaskValues);
  };

  return (
    <div className="space-y-3">
      <Form {...form}>
        <form
          className="relative w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={isReadOnly || isFormLoading}
            className="bg-muted/80 flex w-full flex-col gap-2 rounded-xl px-2 py-1.5 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <div className="min-w-0 lg:w-64 lg:flex-none">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Task title"
                          className="border-border/40 h-9 bg-white px-2 py-0.5 text-sm leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {createErrorMessage ? (
                <Typography className="text-destructive text-xs">
                  {createErrorMessage}
                </Typography>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
              <FormField
                control={form.control}
                name="matterTaskPlanTaskTriggerType"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          resetTriggerFields();
                          setDefaultTriggerValue(
                            value as z.infer<
                              typeof taskPlanTaskFormSchema
                            >["matterTaskPlanTaskTriggerType"],
                          );
                        }}
                      >
                        <SelectTrigger className="border-border/40 h-9 w-36 bg-white px-2 py-0.5 text-xs leading-normal focus:ring-0 focus:ring-offset-0">
                          <SelectValue placeholder="Trigger type" />
                        </SelectTrigger>
                        <SelectContent>
                          {onboardingTriggerTypeOptions.map((option) => (
                            <SelectItem
                              key={option}
                              value={option}
                              disabled={
                                option === "taskCompletion" ||
                                (option === "actionTaken" &&
                                  (areActionOptionsLoading ||
                                    isActionOptionsError ||
                                    actionOptions.length === 0))
                              }
                            >
                              {triggerTypeLabel(option)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              {triggerType === "matterStatus" ? (
                <FormField
                  control={form.control}
                  name="triggerStatusId"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Select
                          value={field.value ?? undefined}
                          onValueChange={(value) => {
                            field.onChange(value);
                          }}
                        >
                          <SelectTrigger className="border-border/40 h-9 w-40 bg-white px-2 py-0.5 text-xs leading-normal focus:ring-0 focus:ring-offset-0">
                            <SelectValue placeholder="Matter status" />
                          </SelectTrigger>
                          <SelectContent>
                            {renderMatterStatusOptions(statuses)}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : null}
              {triggerType === "actionTaken" ? (
                <FormField
                  control={form.control}
                  name="triggerSubitem"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Select
                          value={field.value ?? undefined}
                          onValueChange={(value) => {
                            field.onChange(value);
                          }}
                          disabled={
                            areActionOptionsLoading ||
                            isActionOptionsError ||
                            actionOptions.length === 0
                          }
                        >
                          <SelectTrigger className="border-border/40 h-9 w-40 bg-white px-2 py-0.5 text-xs leading-normal focus:ring-0 focus:ring-offset-0">
                            <SelectValue
                              placeholder={
                                isActionOptionsError
                                  ? "Actions unavailable"
                                  : areActionOptionsLoading
                                    ? "Loading actions..."
                                    : "Action taken"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {actionOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="submit"
                variant="ghost"
                size="xs"
                disabled={isReadOnly || isFormLoading || isMissingAssigneeRole}
                aria-label="Save new task"
              >
                <Check />
              </Button>
            </div>
          </fieldset>

          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Form>
    </div>
  );
}

type TaskPlanTasksSectionProps = {
  taskPlan: MatterTaskPlan;
  statuses: MatterStatus[];
  roles: MatterRole[];
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
};

export function TaskPlanTasksSection({
  taskPlan,
  statuses,
  roles,
  canCreate,
  canUpdate,
  canDelete,
}: TaskPlanTasksSectionProps) {
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<
    "title" | "description" | null
  >(null);
  const [inlineValue, setInlineValue] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteTask, setPendingDeleteTask] =
    useState<MatterTaskPlanTask | null>(null);
  const deleteTask = useDeleteMatterTaskPlanTask({ showToast: false });
  const updateTask = useUpdateMatterTaskPlanTask({ showToast: false });
  const {
    data: tasksData,
    isLoading: isTasksLoading,
    isError: isTasksError,
  } = useQuery(retrieveMatterTaskPlansQueryOptions());
  const {
    actionOptions,
    isLoading: areActionOptionsLoading,
    isError: isActionOptionsError,
  } = useMatterTaskPlanActions();

  const tasks = useMemo(
    () => tasksData?.data.find((plan) => plan.id === taskPlan.id)?.tasks ?? [],
    [taskPlan.id, tasksData?.data],
  );
  const defaultAssigneeRoleId = roles[0]?.id ?? null;

  const closeTaskEditor = () => {
    setActiveTaskId(null);
    setActiveField(null);
    setInlineValue("");
    setInlineError(null);
  };

  const startInlineEdit = (
    task: MatterTaskPlanTask,
    field: "title" | "description",
  ) => {
    if (!canUpdate) return;
    setActiveTaskId(task.id);
    setActiveField(field);
    setInlineValue(field === "title" ? task.title : (task.description ?? ""));
    setInlineError(null);
  };

  const submitInlineEdit = (task: MatterTaskPlanTask) => {
    if (!activeField) return;
    if (!canUpdate) {
      closeTaskEditor();
      return;
    }

    const validation =
      taskPlanTaskInlineSchemas[activeField].safeParse(inlineValue);
    if (!validation.success) {
      setInlineError(
        String(validation.error.errors[0]?.message ?? "Invalid value"),
      );
      return;
    }

    const nextValue = validation.data;
    const currentValue =
      activeField === "title" ? task.title : (task.description ?? "");

    if (nextValue === currentValue) {
      closeTaskEditor();
      return;
    }

    updateTask.mutate({
      id: task.id,
      taskPlanId: task.taskPlanId,
      dateModified: task.dateModified,
      title: activeField === "title" ? (nextValue as string) : task.title,
      description:
        activeField === "description"
          ? (nextValue as string)
          : task.description,
      ...getAssignmentPayload(task),
      triggerStatusId: task.triggerStatusId,
      triggerSubitem: task.triggerSubitem,
      triggerTaskId: undefined,
    });
    closeTaskEditor();
  };

  const handleTriggerTypeChange = (
    task: MatterTaskPlanTask,
    nextType: z.infer<
      typeof taskPlanTaskFormSchema
    >["matterTaskPlanTaskTriggerType"],
  ) => {
    if (!canUpdate) return;
    setActiveTaskId(task.id);
    setInlineError(null);

    let nextMatterStatus = undefined;
    let nextSubitem = undefined;

    if (nextType === "matterStatus") {
      nextMatterStatus = task.triggerStatusId ?? statuses[0]?.id;
      if (!nextMatterStatus) {
        setInlineError("Matter status is required");
        return;
      }
    }

    if (nextType === "actionTaken") {
      nextSubitem =
        actionOptions.find((option) => option.value === task.triggerSubitem)
          ?.value ?? actionOptions[0]?.value;
      if (!nextSubitem) {
        setInlineError("Action taken is required");
        return;
      }
    }

    updateTask.mutate({
      id: task.id,
      taskPlanId: task.taskPlanId,
      dateModified: task.dateModified,
      title: task.title,
      description: task.description,
      ...getAssignmentPayload(task),
      triggerStatusId: nextMatterStatus,
      triggerSubitem: nextSubitem,
      triggerTaskId: undefined,
    });
  };

  const handleTriggerValueChange = (
    task: MatterTaskPlanTask,
    field: "matterStatus" | "actionTaken",
    value: string,
  ) => {
    if (!canUpdate) return;
    setActiveTaskId(task.id);
    setInlineError(null);

    updateTask.mutate({
      id: task.id,
      taskPlanId: task.taskPlanId,
      dateModified: task.dateModified,
      title: task.title,
      description: task.description,
      ...getAssignmentPayload(task),
      triggerStatusId: field === "matterStatus" ? value : undefined,
      triggerSubitem: field === "actionTaken" ? value : undefined,
      triggerTaskId: undefined,
    });
  };

  const handleInlineKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    task: MatterTaskPlanTask,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitInlineEdit(task);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeTaskEditor();
    }
  };

  const handleDeleteTask = async () => {
    if (!pendingDeleteTask) return;
    await deleteTask.mutateAsync({
      id: pendingDeleteTask.id,
      taskPlanId: pendingDeleteTask.taskPlanId,
    });
    setDeleteModalOpen(false);
    setPendingDeleteTask(null);
  };

  return (
    <div className="border-border/40 ml-1 w-full space-y-2 border-l pt-1 pl-3">
      <div className="flex items-center">
        <Typography className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Tasks
        </Typography>
      </div>

      {!defaultAssigneeRoleId ? (
        <Typography className="text-muted-foreground text-xs">
          Add a matter role in settings to create tasks.
        </Typography>
      ) : null}

      {isTasksLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full rounded-lg" />
          <Skeleton className="h-14 w-full rounded-lg" />
        </div>
      ) : isTasksError ? (
        <Typography className="text-muted-foreground text-xs">
          {serverErrorFallbackText}
        </Typography>
      ) : null}

      {tasks.length > 0 ? (
        <div className="space-y-2">
          {tasks.map((task) => {
            const isEditingTitle =
              activeTaskId === task.id && activeField === "title";
            const isEditingDescription =
              activeTaskId === task.id && activeField === "description";
            const triggerType = task.triggerSubitem
              ? "actionTaken"
              : "matterStatus";

            return (
              <div key={task.id} className="flex flex-col gap-2 py-2">
                <div className="flex flex-col gap-2 px-2 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <Typography
                      weight="semibold"
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="text-foreground flex-none">
                        <InlineEditableField
                          isEditing={isEditingTitle}
                          value={inlineValue}
                          displayValue={task.title}
                          canEdit={canUpdate}
                          ariaLabel="Task title"
                          inputWidthClass="w-auto max-w-full"
                          autoSize
                          matchDisplaySize
                          onStartEdit={() => {
                            startInlineEdit(task, "title");
                          }}
                          onChange={(value: string) => {
                            setInlineValue(value);
                            if (inlineError) setInlineError(null);
                          }}
                          onSubmit={() => {
                            submitInlineEdit(task);
                          }}
                          onKeyDown={(
                            event: KeyboardEvent<
                              HTMLInputElement | HTMLTextAreaElement
                            >,
                          ) => {
                            handleInlineKeyDown(event, task);
                          }}
                        />
                      </span>
                      <span className="text-muted-foreground min-w-0 flex-1 text-xs leading-snug">
                        <InlineEditableField
                          isEditing={isEditingDescription}
                          value={inlineValue}
                          displayValue={
                            task.description
                              ? task.description
                              : "No description"
                          }
                          canEdit={canUpdate}
                          ariaLabel="Task description"
                          inputWidthClass="w-auto max-w-full"
                          autoSize
                          textClassName="text-xs"
                          matchDisplaySize
                          onStartEdit={() => {
                            startInlineEdit(task, "description");
                          }}
                          onChange={(value: string) => {
                            setInlineValue(value);
                            if (inlineError) setInlineError(null);
                          }}
                          onSubmit={() => {
                            submitInlineEdit(task);
                          }}
                          onKeyDown={(
                            event: KeyboardEvent<
                              HTMLInputElement | HTMLTextAreaElement
                            >,
                          ) => {
                            handleInlineKeyDown(event, task);
                          }}
                        />
                      </span>
                    </Typography>
                    {activeTaskId === task.id && inlineError ? (
                      <Typography className="text-destructive text-xs">
                        {inlineError}
                      </Typography>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
                    <Select
                      value={triggerType}
                      onValueChange={(value) => {
                        handleTriggerTypeChange(
                          task,
                          value as z.infer<
                            typeof taskPlanTaskFormSchema
                          >["matterTaskPlanTaskTriggerType"],
                        );
                      }}
                      disabled={!canUpdate}
                    >
                      <SelectTrigger className="border-border/40 h-9 w-36 bg-white px-2 py-0.5 text-xs leading-normal focus:ring-0 focus:ring-offset-0">
                        <SelectValue placeholder="Trigger type" />
                      </SelectTrigger>
                      <SelectContent>
                        {onboardingTriggerTypeOptions.map((option) => (
                          <SelectItem
                            key={option}
                            value={option}
                            disabled={
                              option === "taskCompletion" ||
                              (option === "actionTaken" &&
                                (areActionOptionsLoading ||
                                  isActionOptionsError ||
                                  actionOptions.length === 0))
                            }
                          >
                            {triggerTypeLabel(option)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {triggerType === "matterStatus" ? (
                      <Select
                        value={task.triggerStatusId ?? undefined}
                        onValueChange={(value) => {
                          handleTriggerValueChange(task, "matterStatus", value);
                        }}
                        disabled={!canUpdate}
                      >
                        <SelectTrigger className="border-border/40 h-9 w-40 bg-white px-2 py-0.5 text-xs leading-normal focus:ring-0 focus:ring-offset-0">
                          <SelectValue placeholder="Matter status" />
                        </SelectTrigger>
                        <SelectContent>
                          {renderMatterStatusOptions(statuses)}
                        </SelectContent>
                      </Select>
                    ) : null}

                    {triggerType === "actionTaken" ? (
                      <Select
                        value={task.triggerSubitem ?? undefined}
                        onValueChange={(value) => {
                          handleTriggerValueChange(task, "actionTaken", value);
                        }}
                        disabled={
                          !canUpdate ||
                          areActionOptionsLoading ||
                          isActionOptionsError ||
                          actionOptions.length === 0
                        }
                      >
                        <SelectTrigger className="border-border/40 h-9 w-40 bg-white px-2 py-0.5 text-xs leading-normal focus:ring-0 focus:ring-offset-0">
                          <SelectValue placeholder="Action taken" />
                        </SelectTrigger>
                        <SelectContent>
                          {actionOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={!canDelete}
                      onClick={() => {
                        setPendingDeleteTask(task);
                        setDeleteModalOpen(true);
                      }}
                      aria-label={`Delete ${task.title}`}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div />
      )}

      {canCreate ? (
        <div className="space-y-2">
          {tasks.length === 0 ? (
            <Typography className="text-muted-foreground text-xs">
              Add a task to get started.
            </Typography>
          ) : null}
          <AddTaskPlanTaskEditor
            taskPlanId={taskPlan.id}
            statuses={statuses}
            actionOptions={actionOptions}
            areActionOptionsLoading={areActionOptionsLoading}
            isActionOptionsError={isActionOptionsError}
            isReadOnly={!canUpdate}
            defaultAssigneeRoleId={defaultAssigneeRoleId}
            onClose={closeTaskEditor}
          />
        </div>
      ) : null}

      <ActionConfirmationModal
        title="Are you sure you want to delete this task?"
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        afterActionFn={handleDeleteTask}
        onCancel={() => {
          setPendingDeleteTask(null);
        }}
      />
    </div>
  );
}
